import React from 'react';
import PropTypes from 'prop-types';
import {Form, Field} from 'react-final-form';
import Stack from '@mui/material/Stack';
import {
  Box,
  Button,
  Grid,
  Heading,
  IconButton,
  Select,
  Text,
  TextInput,
  useMediaQuery,
} from '@tenancy.nz/ui';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {getFieldProps} from '../utils/helpers';
import rentOptions from '../json/rent_options.json';
import bedroomOptions from '../json/bedroom_options.json';

function PropertyFilterForm({
  countLabel = undefined,
  initialValues = {},
  onSubmit,
  onClear = undefined,
}) {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  const commonSelectProps = {
    native: isMobile,
    MenuProps: {
      style: {
        maxHeight: 350,
      },
    },
  };

  const divider = (
    <Box color="grey.600" borderTop="1px solid currentColor" width="32px" />
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({handleSubmit, form, pristine, submitting, values}) => (
        <form onSubmit={handleSubmit}>
          <Stack gap={8} my={8}>
            <Box>
              <Grid container spacing={4}>
                <Grid item container alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Heading as="h1" weight="600">
                      Browse Properties
                    </Heading>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field name="query">
                      {({input, meta}) => (
                        <TextInput
                          {...getFieldProps({input, meta})}
                          placeholder="Search property address"
                          fullWidth
                          autoComplete="off"
                          endAdornment={
                            initialValues.query?.length ? (
                              <IconButton color="primary" onClick={onClear}>
                                <CloseIcon />
                              </IconButton>
                            ) : (
                              <SearchIcon color="primary" />
                            )
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Grid container spacing={4} alignItems="flex-end">
                <Grid item container xs={12} md={3}>
                  <Grid item xs={12}>
                    Rent
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                    <Field name="rent_from">
                      {({input, meta}) => (
                        <Select
                          {...getFieldProps({input, meta})}
                          placeholder="Any"
                          options={rentOptions}
                          {...commonSelectProps}
                        />
                      )}
                    </Field>
                    {divider}
                    <Field name="rent_to">
                      {({input, meta}) => (
                        <Select
                          {...getFieldProps({input, meta})}
                          hideLabel
                          placeholder="Any"
                          options={rentOptions}
                          {...commonSelectProps}
                        />
                      )}
                    </Field>
                    <Box
                      display={isMobile ? 'none' : 'inline-block'}
                      margin="0 5px">
                      &nbsp;
                    </Box>
                  </Grid>
                </Grid>
                <Grid item container xs={12} md={3}>
                  <Grid item xs={12}>
                    Bedrooms
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                    <Field name="bedrooms_from">
                      {({input, meta}) => (
                        <Select
                          {...getFieldProps({input, meta})}
                          hideLabel
                          placeholder="Any"
                          options={bedroomOptions}
                          {...commonSelectProps}
                        />
                      )}
                    </Field>
                    {divider}
                    <Field name="bedrooms_to">
                      {({input, meta}) => (
                        <Select
                          {...getFieldProps({input, meta})}
                          hideLabel
                          placeholder="Any"
                          options={bedroomOptions}
                          {...commonSelectProps}
                        />
                      )}
                    </Field>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack
                    gap={2}
                    flexDirection={{md: 'row'}}
                    width="100%"
                    mb="2px">
                    <Button
                      color="primary"
                      size="large"
                      fill={isMobile}
                      loading={submitting}
                      disabled={pristine || submitting}
                      type="submit">
                      Search
                    </Button>
                    <Button
                      color="primary"
                      variant="outlined"
                      size="large"
                      fill={isMobile}
                      disabled={Object.keys(values).length === 0}
                      onClick={() => {
                        form.reset();
                        if (onClear) {
                          onClear();
                        }
                      }}>
                      Clear
                    </Button>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}>
                  {countLabel && (
                    <Stack ml="auto" mb={{md: 3}}>
                      <Text weight="500">{countLabel}</Text>
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      )}
    />
  );
}

PropertyFilterForm.propTypes = {
  countLabel: PropTypes.node,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClear: PropTypes.func,
};

export default PropertyFilterForm;
